import React from 'react'

import { addToCheckoutCart, initBedbankSession } from 'actions/CheckoutActions'
import { fetchPropertyRates, updatePropertyPricingData } from 'actions/PropertyActions'
import * as Analytics from 'analytics/analytics'
import { addToCartGTM } from 'analytics/eventDefinitions'
import { addToCart } from 'analytics/snowplow/events'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import ProcessingModal from 'checkout/Components/Booking/Modals/ProcessingModal'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import BedbankChangePriceModal from 'components/OfferPage/Bedbank/Modals/BedbankChangePriceModal'
import BedbankSoldOutModal from 'components/OfferPage/Bedbank/Modals/BedbankSoldOutModal'
import type { ModalContextType } from 'contexts/ModalContext'
import type { ModalElementContextValue } from 'contexts/ModalProvider'
import type {
  OfferPageDispatchAction,
  OfferPageRoomOption,
} from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import {
  generateBedbankPropertyAccommodationCheckoutItems,
  generateLpcPropertyAccommodationCheckoutItems,
} from 'lib/checkout/accommodation/cart'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { floatify } from 'lib/maths/mathUtils'
import { sum } from 'lib/array/arrayUtils'
import type { AppDispatch } from 'src/client/store'
import { addToUserCart } from 'actions/AuthActions'

type SessionResponse = Awaited<ReturnType<typeof initBedbankSession>>

function isValidSession(session: SessionResponse): session is Extract<SessionResponse, { sessionId: string }> {
  return !!('sessionId' in session)
}

interface ChangedRate {
  rate: App.BedbankPropertyRate
  newGeneralPrice?: number
  newPropertyFees?: number
  rooms: Array<App.Occupants>
}

function calculatePriceChange(changedRates: Array<ChangedRate>): {
  oldDisplayPrice: number
  newDisplayPrice: number
  oldPropertyFees: number
  newPropertyFees: number
} {
  const oldDisplayPrice = sum(changedRates, ch => ch.rate.total.sellingPrice)
  const newDisplayPrice = sum(changedRates, ch => ch.newGeneralPrice ?? ch.rate.total.sellingPrice)
  const oldPropertyFees = sum(changedRates, ch => ch.rate.total.propertyFees)
  const newPropertyFees = sum(changedRates, ch => ch.newPropertyFees ?? ch.rate.total.propertyFees)
  return {
    oldDisplayPrice,
    newDisplayPrice,
    oldPropertyFees,
    newPropertyFees,
  }
}

/**
 * Dispatches an action to patch changed rates in the offer store and returns semi-patched rates
 * that can be used to perform a new bedbank service enquiry request.
 */
function updateChangedRates(dispatch: AppDispatch, {
  propertyId,
  checkIn,
  checkOut,
  utmContent,
  roomTypeId,
  roomRateId,
  changedRates,
}: {
  propertyId: string
  checkIn: string
  checkOut: string
  utmContent: string | undefined
  roomTypeId: string | undefined
  roomRateId: string | undefined
  changedRates: Array<ChangedRate>,
}): Array<App.BedbankPropertyRate> {
  return changedRates.map(({ rate, newGeneralPrice, newPropertyFees, rooms }) => {
    const resolvedGeneralPrice = newGeneralPrice ?? rate.total.sellingPrice
    const resolvedPropertyFees = newPropertyFees ?? rate.total.propertyFees

    if (
      resolvedGeneralPrice === rate.total.sellingPrice &&
      resolvedPropertyFees === rate.total.propertyFees
    ) {
      // Not all rates have necessarily changed.
      return rate
    }

    dispatch(updatePropertyPricingData(propertyId, {
      checkIn,
      checkOut,
      occupancies: rooms,
      utmContent,
      roomTypeId,
      roomRateId,
      roomId: rate.roomId,
      rateId: rate.rateId,
      newGeneralPrice: resolvedGeneralPrice,
      newPropertyFees: resolvedPropertyFees,
    }))

    // We don't need to be as thorough here; we only need to patch the fields that're used to perform
    // a bedbank service enquiry request.
    return {
      ...rate,
      total: {
        ...rate.total,
        sellingPrice: resolvedGeneralPrice,
        propertyFees: resolvedPropertyFees,
      }
    }
  })
}

interface LpcOfferAndRate {
  type: 'lpc'
  offer: App.HotelOffer
  rate: App.LpcPropertyRate
}

interface BedbankOfferAndRate {
  type: 'bedbank'
  offer: App.BedbankOffer
  rate: App.BedbankPropertyRate
}

export type OfferAndRate = LpcOfferAndRate | BedbankOfferAndRate

interface SingleRoomArgs {
  shouldAddToCart: boolean
  dispatch: AppDispatch
  offerPageDispatch: React.Dispatch<OfferPageDispatchAction>
  modalElementContext: ModalElementContextValue<never>
  showModal: ModalContextType
  propertyId: string
  offerAndRate: OfferAndRate
  optionNumber: number
  originAirportCode: string | undefined
  checkIn: string
  checkOut: string
  duration: number
  rooms: Array<App.Occupants>
  regionCode: string
  currency: string
  source: string
  utmContent: string | undefined
  roomOption: OfferPageRoomOption | undefined
  isSpoofed: boolean
  roomListId: string
  roomPosition: number
  rateListId: string
}

export async function propertySingleRoomBookNowOrAddToCartCallback(args: SingleRoomArgs) {
  const {
    shouldAddToCart,
    dispatch,
    offerPageDispatch,
    modalElementContext,
    showModal,
    propertyId,
    offerAndRate,
    optionNumber,
    checkIn,
    checkOut,
    duration,
    rooms,
    regionCode,
    currency,
    source,
    utmContent,
    roomOption,
    isSpoofed,
    roomListId,
    roomPosition,
    rateListId,
  } = args
  const { type, offer, rate } = offerAndRate
  const period = { checkIn, checkOut, duration }
  const accommodationItems: Array<App.Checkout.InstantBookingLEHotelItem | App.Checkout.BedbankHotelItem> = []
  if (type === 'lpc') {
    accommodationItems.push(...generateLpcPropertyAccommodationCheckoutItems(
      offer,
      rate,
      period,
      rooms,
    ))
  } else if (type === 'bedbank') {
    if (!shouldAddToCart) {
      let closeProcessingModal: any
      showModal((resolve) => {
        closeProcessingModal = resolve
        return <ProcessingModal message="Please wait..." />
      })

      try {
        // For each room, create a separate session in parallel, so each room becomes a separate order item.
        const roomSessions = await Promise.all(rooms.map(async(room) => {
          const session = await initBedbankSession(dispatch, {
            propertyId: offer.id,
            roomTypeId: rate.bedbankRoomId,
            roomRateId: rate.bedbankRateId,
            rateGroupId: rate.rateGroupId,
            bedGroupId: rate.bedGroups[0].id,
            isFlightBundle: rate.isFlightBundle,
            csBooking: isSpoofed,
            region: regionCode,
            rooms: [room],
            checkIn,
            checkOut,
            price: floatify(rate.total.sellingPrice / rooms.length),
            propertyFees: rate.total.propertyFees ? floatify(rate.total.propertyFees / rooms.length) : undefined,
            source,
          })

          return { room, session }
        }))

        closeProcessingModal()

        if (roomSessions.some(rs => !isValidSession(rs.session))) {
          // CHANGE OF PRICE - CANNOT PROCEED UNTIL USER ACCEPTS PRICE
          const changedRates = roomSessions.map(rs => ({
            rate,
            newGeneralPrice: rs.session.price,
            newPropertyFees: 'propertyFees' in rs.session ? rs.session.propertyFees : undefined,
            rooms: [rs.room],
          }))

          const hasUserAcceptedNewRates = await showModal<boolean | undefined>(<BedbankChangePriceModal
            {...calculatePriceChange(changedRates)}
          />)
          if (hasUserAcceptedNewRates) {
            const newRates = updateChangedRates(dispatch, {
              propertyId,
              checkIn,
              checkOut,
              changedRates,
              utmContent,
              roomTypeId: roomOption?.roomTypeId,
              roomRateId: roomOption?.roomRateId,
            })
            return propertySingleRoomBookNowOrAddToCartCallback({
              ...args,
              offerAndRate: { ...offerAndRate, rate: newRates[0] } // Book for multi-same-room bookings, so newRates[0] applies to all.
            })
          }
          // Even if the user doesn't accept the new rates, there's no point displaying the stale rates.
          dispatch(fetchPropertyRates(propertyId, {
            checkIn,
            checkOut,
            occupancies: rooms,
            utmContent,
            roomTypeId: roomOption?.roomTypeId,
            roomRateId: roomOption?.roomRateId,
            refresh: true,
          }))
          return
        }

        // All sessions valid - proceed with checkout
        if (roomSessions.every(rs => isValidSession(rs.session))) {
          const bedbankItems = roomSessions.map((rs) => {
            const session = rs.session as Extract<SessionResponse, { sessionId: string }>
            const items = generateBedbankPropertyAccommodationCheckoutItems(
              offer,
              session.sessionId,
              rate,
              period,
              [rs.room],
            )
            return items[0]
          })
          accommodationItems.push(...bedbankItems)
        }
      } catch {
        closeProcessingModal()
        // We've hit an unrecoverable error and expect at least one of the now-stale rates to be
        // blocklisted, so let's refresh.
        dispatch(fetchPropertyRates(propertyId, {
          checkIn,
          checkOut,
          occupancies: rooms,
          utmContent,
          roomTypeId: roomOption?.roomTypeId,
          roomRateId: roomOption?.roomRateId,
          refresh: true,
        }))
        showModal(<BedbankSoldOutModal />)
        return
      }
    } else {
      // When adding to user cart, no session is created yet.
      // Generate items without sessionId - sessions will be created later.
      accommodationItems.push(...generateBedbankPropertyAccommodationCheckoutItems(
        offer,
        undefined,
        rate,
        period,
        rooms,
      ))
    }
  }

  const checkoutItems = [...accommodationItems]

  offerPageDispatch({ type: OfferPageActions.closeFilter })
  modalElementContext?.resolve()

  if (!shouldAddToCart) {
    dispatch(addToCheckoutCart(checkoutItems))
  }

  // TODO(Xiao): Confirm this is the price that Data want.
  fireEcommerceGTMEvent(addToCartGTM(offer, currency, rate.luxPlusTotal?.displayPrice ?? rate.total.displayPrice, sumUpOccupancies(rooms), 'hotel'))
  // TODO(Xiao): Figure out why the other rate types don't fire this event for flights.
  // NB: This event doesn't appear to fire immediately; it seems to be waiting on
  // `checkoutStateIsReady()`.
  Analytics.trackEvent(addToCart({
    roomListId,
    roomPosition,
    rateListId,
    ratePosition: optionNumber,
    type: 'property',
    rate,
    currency,
  }))

  if (shouldAddToCart) {
    dispatch(addToUserCart(checkoutItems))
    return
  }

  dispatch(navigateToCheckout())
}
